import React from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CheckCircle, Clock, Shield, Palette, Sparkles } from 'lucide-react';
import BespokeGildingGlazingSection from '../components/framing/BespokeGildingGlazingSection';

const PictureFramingPage = () => {
  const framingLevels = [
    {
      icon: Sparkles,
      title: "Museum Level Framing",
      description: "The highest standard of preservation framing using museum-grade materials, UV-protective glass, and archival mounting techniques. Perfect for valuable artworks and heirlooms.",
      features: ["Museum Quality Mount Board", "UV-Protective Glass", "Archival Materials", "Conservation Standards"]
    },
    {
      icon: Shield,
      title: "Conservation Level Framing",
      description: "Professional conservation framing that ensures long-term protection for your artworks using acid-free materials and protective glazing.",
      features: ["Acid-Free Mount Board", "Museum Glass Available", "Archival Quality", "UV Protection"]
    },
    {
      icon: Palette,
      title: "Commended Level Framing",
      description: "High-quality traditional framing that balances aesthetics and protection, suitable for photographs, prints, and contemporary artworks.",
      features: ["Quality Materials", "Wide Frame Selection", "Professional Mounting", "Cost-Effective"]
    },
    {
      icon: CheckCircle,
      title: "Art Restoration",
      description: "Expert restoration services for damaged or aged artworks, bringing them back to their original glory with care and precision.",
      features: ["Expert Assessment", "Careful Restoration", "Color Matching", "Structural Repair"]
    }
  ];

  const services = [
    "Free advice & quotes on framing that is best for you & your art",
    "Conservation service, archival framing with museum glass and UV acrylics available",
    "For individuals, Artists, Galleries, Photographers & Interior Designers",
    "Picture Framing in Hampstead, North West London NW3",
    "Wide range of wood frames in gold, silver, and unique hand-coloured finishes",
    "Single and double mount formats in various colours",
    "Acid-free mounts to protect your artwork",
    "Museum quality mount board, museum glass, float glass, and non-reflective glass"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-0 px-6 border-b border-border">
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-6 font-sans">
              Professional Services
            </p>
            
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-foreground mb-8 leading-[1.1]">
              Picture Framing
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl font-sans leading-relaxed mb-8">
              Traditional bespoke picture framing in Hampstead, North West London. 
              We frame to suit all tastes and budgets, from museum-level conservation 
              to contemporary presentation.
            </p>

            {/* Lead Time Badge */}
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-foreground text-background mb-12">
              <Clock className="w-5 h-5" />
              <p className="font-sans text-sm uppercase tracking-wider">Current Lead Time: 7 Working Days</p>
            </div>
          </motion.div>

          {/* Hero image — archival installation shot */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="relative -mx-6 md:mx-0"
          >
            <img
              src="/gallery-archive/framing/Framing_banner1.jpg"
              alt="Picture framing at Zebra One Gallery Hampstead"
              loading="eager"
              className="w-full h-[320px] md:h-[460px] object-cover"
              data-testid="picture-framing-hero-image"
            />
          </motion.div>
        </div>
      </section>

      {/* Installation gallery */}
      <section className="py-20 px-6 border-b border-border">
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4 font-sans">
              In the Workshop
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">
              Craftsmanship You Can See
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl font-sans leading-relaxed">
              Works framed in-house at Zebra One Gallery — from traditional wood mouldings through
              to bespoke hand-coloured finishes, each treated individually to its piece.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { src: "/gallery-archive/framing/framing-banner-1.jpg", alt: "Bespoke wood frames at Zebra One Gallery" },
              { src: "/gallery-archive/framing/Picture-framing-at-zebra-one-gallery-london.jpg", alt: "Marc Chagall framed work at Zebra One Gallery" },
              { src: "/gallery-archive/framing/framing-banner-3.jpg", alt: "Framed works installed at Zebra One Gallery Hampstead" },
            ].map((img, index) => (
              <motion.div
                key={img.src}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.08 }}
                className="overflow-hidden group"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full aspect-[380/245] object-cover transition-transform duration-700 group-hover:scale-105"
                  data-testid={`picture-framing-thumb-${index}`}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Frame */}
      <section className="py-20 px-6 border-b border-border">
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">
              What We Frame
            </h2>
            
            <p className="text-lg text-muted-foreground max-w-3xl font-sans leading-relaxed mb-12">
              Zebra One Gallery frames a wide range of works from original oil and acrylic artworks, 
              photographs, prints, certificates, and award documents. We also offer a conservation service.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {['Original Oil Paintings', 'Acrylic Artworks', 'Photography', 'Fine Art Prints', 'Certificates', 'Award Documents', 'Mixed Media', 'Conservation Projects'].map((item, index) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <CheckCircle className="w-5 h-5 text-foreground flex-shrink-0 mt-1" />
                  <p className="font-sans text-muted-foreground">{item}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <BespokeGildingGlazingSection />
      <section className="py-20 px-6 bg-muted/20">
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">
              Framing Levels
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl font-sans leading-relaxed">
              Choose the level of protection and presentation that suits your artwork and budget.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {framingLevels.map((level, index) => {
              const Icon = level.icon;
              return (
                <motion.div
                  key={level.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-background p-8 border border-border group hover:border-foreground transition-colors"
                >
                  <Icon className="w-12 h-12 mb-6 text-foreground" />
                  
                  <h3 className="font-serif text-2xl text-foreground mb-4">
                    {level.title}
                  </h3>
                  
                  <p className="text-muted-foreground font-sans mb-6 leading-relaxed">
                    {level.description}
                  </p>

                  <ul className="space-y-3">
                    {level.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <CheckCircle className="w-4 h-4 text-foreground flex-shrink-0 mt-1" />
                        <span className="text-sm text-muted-foreground font-sans">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Materials & Options */}
      <section className="py-20 px-6 border-b border-border">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Frame Options */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-serif text-3xl text-foreground mb-6">
                Frame Options
              </h2>
              
              <p className="text-muted-foreground font-sans mb-8 leading-relaxed">
                Zebra One Gallery Hampstead stocks a wide range of wood available in gold, silver, 
                and unique hand-coloured one-off frames to complement your interior.
              </p>

              <div className="space-y-4">
                {['Gold Leaf Frames', 'Silver Leaf Frames', 'Hand-Coloured Unique Frames', 'Natural Wood Finishes', 'Contemporary Styles', 'Traditional Profiles'].map((option) => (
                  <div key={option} className="flex items-center gap-3 pb-4 border-b border-border">
                    <div className="w-2 h-2 bg-foreground"></div>
                    <p className="font-sans text-foreground">{option}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Mount & Glass Options */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-serif text-3xl text-foreground mb-6">
                Mounts & Glass
              </h2>
              
              <p className="text-muted-foreground font-sans mb-8 leading-relaxed">
                Only the best materials are used, including museum quality mount board and 
                various glass options. All mounts are acid-free to protect your artwork.
              </p>

              <div className="space-y-6">
                <div>
                  <h3 className="font-sans text-sm uppercase tracking-wider text-foreground mb-3">Mount Options</h3>
                  <ul className="space-y-2">
                    <li className="text-muted-foreground font-sans">• Single Mount Format</li>
                    <li className="text-muted-foreground font-sans">• Double Mount Format</li>
                    <li className="text-muted-foreground font-sans">• Wide Range of Colours</li>
                    <li className="text-muted-foreground font-sans">• Acid-Free Museum Quality</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-sans text-sm uppercase tracking-wider text-foreground mb-3">Glass Options</h3>
                  <ul className="space-y-2">
                    <li className="text-muted-foreground font-sans">• Museum Glass (UV Protection)</li>
                    <li className="text-muted-foreground font-sans">• Float Glass</li>
                    <li className="text-muted-foreground font-sans">• Non-Reflective Glass</li>
                    <li className="text-muted-foreground font-sans">• UV-Protective Acrylics</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 px-6 bg-muted/20">
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-12">
              Our Services
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {services.map((service, index) => (
                <motion.div
                  key={service}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                  className="flex items-start gap-4"
                >
                  <CheckCircle className="w-5 h-5 text-foreground flex-shrink-0 mt-1" />
                  <p className="font-sans text-muted-foreground leading-relaxed">{service}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 border-t border-border">
        <div className="max-w-[1400px] mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">
              Ready to Frame Your Artwork?
            </h2>
            
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-12 font-sans leading-relaxed">
              For best results, we recommend that you bring your artworks to us so we can 
              discuss your requirements and give you the best advice.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/contact">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-6 py-3 bg-foreground text-background font-sans text-sm uppercase tracking-[0.2em]"
                >
                  Get a Free Quote
                </motion.button>
              </a>
              
              <a href="tel:02077948949">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-6 py-3 border border-foreground text-foreground font-sans text-sm uppercase tracking-[0.2em]"
                >
                  Call: 020 7794 8949
                </motion.button>
              </a>
            </div>

            <div className="mt-12 pt-12 border-t border-border">
              <p className="text-sm text-muted-foreground font-sans">
                <strong className="text-foreground">Zebra One Gallery</strong> · Perrins Court, 73a Hampstead High Street · London NW3 1QH
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PictureFramingPage;
